import styles from "./depreciation.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as graphUtil from "shared/graphs/util"
import Line from "story/components/graphs/line"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, value],
[year, value],
[year, value]
]
*/

const getAnnualDepreciation = (initialValue, salvage, depreciationRate) => depreciationRate * (initialValue - salvage)

const genData = (initialValue, years, annualDepreciation) => {
  let value = initialValue
  return util.xAxisArray(years).reduce((memo, years, idx) => {
    value -= annualDepreciation
    memo.push([years, value])
    return memo
  }, [])
}

const calcThings = ({initialValue, salvage, years}) => {
  const depreciationRate = 1 / years
  return {
    depreciableAssetValue: initialValue - salvage,
    yearlyDepreciationRate: depreciationRate,
    annualDepreciation: getAnnualDepreciation(initialValue, salvage, depreciationRate)
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {initialValue, salvage, years, resetKey} = state
    const {id, theme} = getProps()
    const {depreciableAssetValue, yearlyDepreciationRate, annualDepreciation} = calcThings(state)

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "years"
      },
      {
        name: "Asset Value",
        key: "value"
      }
    ]

    const accessibilityTableData = genData(initialValue, years, annualDepreciation).map((row, idx) => ({
      years: idx,
      value: util.formatDollarsNoCents(row[1])
    }))

    const accessibilityTableLabel = "Asset Depreciation"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer className={styles["inputs"]}>
              <inputs.Input id={id} title="Initial Purchase Price" value={initialValue} onChange={updateState("initialValue", util.fromString)} />
              <inputs.Input
                id={id}
                title="Salvage Value"
                value={salvage}
                onChange={updateState("salvage", util.fromString)}
                tooltipText="What you estimate you can sell the asset for when you are done using it. A quick blue-book/internet search may help you guess more accurately."
              />
              <inputs.RangeInput
                id={id}
                title="Life of Asset"
                onChange={updateState("years", util.fromString)}
                initialValue={years}
                min={1}
                max={30}
                step={1}
                units="years"
                valueType="years-full"
                tooltipText="How long the asset will last/how long you plan to use it."
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <Line
                values={genData(initialValue, years, annualDepreciation)}
                yAxis="Asset Value"
                xAxis="Years from Now"
                formatter="currency"
                tooltip={["Year", {name: "Asset Value", type: "currency", color: graphUtil.getColor(theme, 1)}]}
                type="calculator"
                color={graphUtil.getColor(theme, 1)}
                disabled={initialValue < salvage}
                customTooltipStyles={{rows: 2}}
                ariaLabel="This line graph visually represents how much your asset depreciates over time, based on your input. Read below to find the total depreciation value, the yearly depreciation rate, and the annual depreciation amount."
              />
            </shared.ChartContainer>
            <shared.SummaryContainer>
              <callouts.Callout>
                <callouts.Detail
                  label="Depreciable Asset Value"
                  result={util.formatDollarsNoCents(util.ifNegative(depreciableAssetValue))}
                  tooltipText="The historical purchase price (what you actually paid for the asset) minus the salvage value of the asset (what you think you'll get for it when you are done with it)."
                  dataTestId="depreciable-asset-value"
                />
                <callouts.Detail label="Yearly Depreciation Rate" result={`${util.round(yearlyDepreciationRate * 100)}%`} dataTestId="yearly-depreciation" />
                <callouts.Detail label="Annual Depreciation" result={util.formatDollarsNoCents(annualDepreciation)} dataTestId="annual-depreciation" />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const things = calcThings(state)
      return Object.assign(state, things, {
        yearlyDepreciationRate: util.round(things.yearlyDepreciationRate, 4)
      })
    },
    defaultProps: {
      id: "depreciation-calculator",
      className: "early-payoff",
      title: "Asset Depreciation Calculator",
      subtitle: "See how much your asset will be worth using the straight line depreciation formula."
    },
    defaultState: {
      initialValue: 40000,
      salvage: 10000,
      years: 12
    }
  }
)
